import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AnonymousPreview from '@/components/common/anonymous-preview/AnonymousPreview';
import { PageSEO } from '@/components/common/blog/elements/SEO';
import PageLayout from '../common/page-layout/PageLayout';
import Image from 'next/image';
import { PurchasingOptions } from '../home-page/purchasing-options/PurchasingOptions';
import { SectionDetails } from '@/app/types/marketing/SectionDetails';
import Link from 'next/link';
import foreignLanguageImage from 'public/foreign_language_white.png';
import historyImage from 'public/history_white.png';
import medicineImage from 'public/medicine_white.png';
import computerScienceImage from 'public/computer_science_white.png';
import biologyImage from 'public/biology_white.png';

interface IProps {
  title: string;
  description: string;
  imageSection: SectionDetails;
  featureSection: SectionDetails;
  isYoutube?: boolean;
  isWikipedia?: boolean;
}

export default function GeneratorLandingPage(props: IProps) {
  return (
    <PageLayout>
      <PageSEO title={props.title} description={props.description} />
      <div className="bg-gray-900">
        <div className="relative mb-8 md:isolate md:overflow-x-hidden">
          <div className="hidden md:block">
            <svg
              aria-hidden="true"
              className="absolute inset-0 -z-10 h-full w-full stroke-white/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
            >
              <defs>
                <pattern
                  x="50%"
                  y={-1}
                  id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
                  width={200}
                  height={200}
                  patternUnits="userSpaceOnUse"
                >
                  <path d="M.5 200V.5H200" fill="none" />
                </pattern>
              </defs>
              <svg x="50%" y={-1} className="overflow-visible fill-gray-800/20">
                <path
                  d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                  strokeWidth={0}
                />
              </svg>
              <rect
                fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
                width="100%"
                height="100%"
                strokeWidth={0}
              />
            </svg>

            <div
              aria-hidden="true"
              className="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] md:left-48 md:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
            >
              <div
                style={{
                  clipPath:
                    'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)'
                }}
                className="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-20"
              />
            </div>
          </div>

          <div className="mx-auto flex max-w-7xl flex-col px-6 py-6 pb-24 pt-16 sm:pb-24 md:py-12 md:pt-40 lg:mb-12 lg:flex lg:flex-row lg:px-8 lg:py-12 lg:pt-40">
            <div className="mx-auto max-w-2xl flex-1 flex-shrink-0 text-center md:text-left lg:mx-0 lg:mt-24 lg:max-w-xl">
              <h1 className="mt-10 text-4xl font-bold tracking-tight text-white sm:text-5xl">
                {props.title}
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                {props.description}
              </p>
            </div>
            <div className="flex min-h-[380px] justify-center py-6 pt-20 text-center md:py-16 lg:w-3/5 lg:min-w-[446px] lg:py-12 lg:pt-24">
              <div style={{ zIndex: 500 }} className="w-full lg:w-3/5">
                <AnonymousPreview
                  isYoutube={props.isYoutube}
                  isWikipedia={props.isWikipedia}
                  hideAlternateOptions
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mx-auto mb-24 mt-24 px-6 opacity-30 lg:px-8">
          <h2 className="text-center text-lg font-semibold leading-8 text-white">
            Trusted by students for every subject
          </h2>
          <div className="mx-auto mt-8 grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-12 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 sm:gap-y-14 lg:mx-0 lg:max-w-none lg:grid-cols-5">
            <Image
              alt="Foreign Language"
              src={foreignLanguageImage}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="History"
              src={historyImage}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="Medicine"
              src={medicineImage}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="Computer Science"
              src={computerScienceImage}
              className="col-span-2 max-h-12 w-full object-contain sm:col-start-2 lg:col-span-1"
            />
            <Image
              alt="Biology"
              src={biologyImage}
              className="col-span-2 col-start-2 max-h-12 w-full object-contain sm:col-start-auto lg:col-span-1"
            />
          </div>
        </div>
        <div className="overflow-hidden py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
              <div className="lg:pr-8 lg:pt-4">
                <div className="lg:max-w-lg">
                  <h2 className="text-base font-semibold leading-7 text-primary">
                    {props.imageSection.miniText}
                  </h2>
                  <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                    {props.imageSection.title}
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-300">
                    {props.imageSection.description}
                  </p>
                  <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-300 lg:max-w-none">
                    {props.imageSection.features.map((feature) => (
                      <div key={feature.name} className="relative pl-9">
                        <dt className="inline font-semibold text-white">
                          <feature.icon
                            aria-hidden="true"
                            className="absolute left-1 top-1 h-5 w-5 text-primary"
                          />
                          {feature.name}
                        </dt>{' '}
                        <dd className="inline">{feature.description}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              <Image
                alt="Word to Flashcards screenshot"
                src={props.imageSection.image}
                className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-white/10 sm:w-[70rem] md:-ml-4 lg:-ml-0"
              />
            </div>
          </div>
        </div>
        <div className="bg-gray-900 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-primary">
                {props.featureSection.miniText}
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {props.featureSection.title}
              </p>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                {props.featureSection.description}
              </p>
            </div>
            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                {props.featureSection.features.map((feature) => (
                  <div key={feature.name} className="flex flex-col">
                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                      <feature.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-none text-primary"
                      />
                      {feature.name}
                    </dt>
                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                      <p className="flex-auto">{feature.description}</p>
                      <p className="mt-6">
                        <Link
                          href={'/'}
                          className="text-sm font-semibold leading-6 text-primary"
                        >
                          Learn more <span aria-hidden="true">→</span>
                        </Link>
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
        <section className="bg-gray-50">
          <PurchasingOptions />
        </section>
      </div>
    </PageLayout>
  );
}
